/**
 * The turn line a training page shows while live, as a pure state machine.
 *
 * Inputs are only the audio the client already has: audible customer chunks
 * (playback) and audible microphone chunks (the rep). Each transition names at
 * most ONE timeout for the next timed event; the hook clears the previous
 * timeout on every transition, so there is never more than one pending.
 *
 *   ai_speaking --customer_done--> your_turn
 *   your_turn   --rep_audible----> your_turn (+ rep_quiet timeout)
 *   your_turn   --rep_quiet------> processing (+ processing_slow timeout)
 *   processing  --processing_slow> slow (+ processing_stalled timeout)
 *   slow        --processing_stalled> stalled
 *   processing | slow | stalled --rep_audible--> your_turn (still talking)
 *   any         --customer_audible--> ai_speaking
 *
 * "The rep has spoken" needs no flag: a pending rep_quiet timeout is it. The
 * mic is ignored while the AI speaks, so its echo can never end a turn.
 */

export type TurnState = 'ai_speaking' | 'your_turn' | 'processing' | 'slow' | 'stalled';

type TimedTurnEventType = 'customer_done' | 'rep_quiet' | 'processing_slow' | 'processing_stalled';

export type TurnEvent =
  | { type: 'customer_audible'; playbackRemainingMs: number }
  | { type: 'rep_audible' }
  | { type: TimedTurnEventType };

export interface TurnTransition {
  turn: TurnState;
  timeout: { event: TimedTurnEventType; delayMs: number } | null;
}

export const INITIAL_TURN: TurnState = 'your_turn';

/** Grace after playback drains, so a breath between sentences does not flicker "Your Turn". */
export const CUSTOMER_SPEAKING_TAIL_MS = 300;
/** Rep quiet this long after speaking: their turn is over, the reply is being made. */
export const REP_TURN_END_MS = 1200;
/** Processing this long: "Still working on it...". */
export const PROCESSING_SLOW_MS = 8000;
/** Processing this long in total: "Taking longer than usual...". */
export const PROCESSING_STALLED_MS = 20000;

/** The next state and timeout, or null to ignore the event (current timeout kept). */
export function transitionTurn(turn: TurnState, event: TurnEvent): TurnTransition | null {
  switch (event.type) {
    case 'customer_audible':
      return {
        turn: 'ai_speaking',
        timeout: { event: 'customer_done', delayMs: Math.max(0, event.playbackRemainingMs) + CUSTOMER_SPEAKING_TAIL_MS },
      };
    case 'customer_done':
      return turn === 'ai_speaking' ? { turn: 'your_turn', timeout: null } : null;
    case 'rep_audible':
      return turn === 'ai_speaking' ? null : { turn: 'your_turn', timeout: { event: 'rep_quiet', delayMs: REP_TURN_END_MS } };
    case 'rep_quiet':
      return turn === 'your_turn'
        ? { turn: 'processing', timeout: { event: 'processing_slow', delayMs: PROCESSING_SLOW_MS } }
        : null;
    case 'processing_slow':
      return turn === 'processing'
        ? { turn: 'slow', timeout: { event: 'processing_stalled', delayMs: PROCESSING_STALLED_MS - PROCESSING_SLOW_MS } }
        : null;
    case 'processing_stalled':
      return turn === 'slow' ? { turn: 'stalled', timeout: null } : null;
    default:
      return null;
  }
}
